import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import {
    OnboardingLayout,
    OnboardingDataCard,
    EditInput,
    type OnboardingStep,
    type BadgeStatus,
} from "@vanyshr/ui/components/onboarding";
import { cx } from "@/utils/cx";
import { User, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { allowLocalRouteBypass } from "@/lib/env";
import { patchDevProgress } from "@/lib/dev-user";
import { loadLocalOnboardingSeed } from "@/lib/local-onboarding-seed";

export interface AliasItem {
    id: string;
    name: string;
    status: BadgeStatus;
    isNew?: boolean;
}

export function OnboardingAliases() {
    const [items, setItems] = useState<AliasItem[]>([]);
    const [profileId, setProfileId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState("");

    const navigate = useNavigate();

    // -----------------------------------------------------------------------
    // Load from DB
    // -----------------------------------------------------------------------
    useEffect(() => {
        async function load() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                if (allowLocalRouteBypass()) {
                    const seed = loadLocalOnboardingSeed();
                    setItems((seed?.aliases ?? []).map((name, i) => ({
                        id: `local-alias-${i}`,
                        name,
                        status: "pending" as BadgeStatus,
                    })));
                }
                setIsLoading(false);
                return;
            }

            const { data: profile } = await supabase
                .from("user_profiles")
                .select("id")
                .eq("auth_user_id", user.id)
                .single();

            if (!profile) { setIsLoading(false); return; }
            setProfileId(profile.id);

            const { data: aliases } = await supabase
                .from("user_aliases")
                .select("id, name, user_confirmed_status")
                .eq("user_id", profile.id)
                .eq("is_active", true)
                .order("created_at");

            if (aliases) {
                setItems(aliases.map((a) => ({
                    id:     a.id,
                    name:   a.name,
                    status: a.user_confirmed_status === "confirmed" ? "confirmed" : "pending" as BadgeStatus,
                })));
            }
            setIsLoading(false);
        }
        load();
    }, []);

    // -----------------------------------------------------------------------
    // DB helpers
    // -----------------------------------------------------------------------
    const upsertAlias = useCallback(async (item: AliasItem) => {
        if (!profileId) return;
        const now = new Date().toISOString();
        if (item.isNew) {
            const { data } = await supabase
                .from("user_aliases")
                .insert({
                    user_id:               profileId,
                    name:                  item.name,
                    user_confirmed_status: "confirmed",
                    confirmed_at:          now,
                    source:                "user_input",
                })
                .select("id")
                .single();
            if (data) {
                setItems((prev) =>
                    prev.map((a) => a.id === item.id ? { ...a, id: data.id, isNew: false } : a)
                );
            }
        } else {
            await supabase
                .from("user_aliases")
                .update({ name: item.name, user_confirmed_status: "confirmed", confirmed_at: now })
                .eq("id", item.id);
        }
    }, [profileId]);

    const deleteAlias = useCallback(async (id: string, isNew?: boolean) => {
        if (isNew) return;
        const { error } = await supabase
            .from("user_aliases")
            .update({ is_active: false })
            .eq("id", id);
        if (error) console.error("[Onboarding] Alias delete failed:", error.message);
    }, []);

    // -----------------------------------------------------------------------
    // Interaction handlers
    // -----------------------------------------------------------------------
    const openEdit = (item: AliasItem) => {
        setActiveId(item.id);
        setEditingId(item.id);
        setEditValue(item.name);
    };

    const handleUpdate = async (id: string) => {
        const updated = items.map((a) =>
            a.id === id
                ? { ...a, name: editValue.trim() || a.name, status: "confirmed" as BadgeStatus }
                : a,
        );
        setItems(updated);
        setEditingId(null);
        setActiveId(null);

        const item = updated.find((a) => a.id === id);
        if (item) await upsertAlias(item);
    };

    const handleDelete = async (id: string) => {
        const item = items.find((a) => a.id === id);
        setItems((prev) => prev.filter((a) => a.id !== id));
        if (activeId === id) setActiveId(null);
        setEditingId(null);
        if (item) await deleteAlias(id, item.isNew);
    };

    const handleAdd = () => {
        const tempId = `new-${Date.now()}`;
        setItems((prev) => [...prev, { id: tempId, name: "", status: "pending", isNew: true }]);
        setActiveId(tempId);
        setEditingId(tempId);
        setEditValue("");
    };

    const handleConfirmAndContinue = async () => {
        // Bulk-confirm all active items so none are left as 'unverified'
        if (profileId) {
            await supabase
                .from("user_aliases")
                .update({
                    user_confirmed_status: "confirmed",
                    confirmed_at:          new Date().toISOString(),
                })
                .eq("user_id", profileId)
                .eq("is_active", true);
        }
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            await supabase
                .from("user_profiles")
                .update({ onboarding_step: 4 })
                .eq("auth_user_id", user.id);
        }
        patchDevProgress({ step: 4 });
        setActiveId(null);
        setEditingId(null);
        navigate("/onboarding/addresses");
    };

    if (isLoading) {
        return (
            <OnboardingLayout
                currentStep={"aliases" satisfies OnboardingStep}
                completedSteps={["basic", "emails", "phone"]}
                title="Aliases"
                subtitle="Click on Field to Edit"
                onDashboardNavigate={() => navigate("/scanning-started")}
                footer={null}
            >
                <div className="flex items-center justify-center py-16">
                    <div className="w-6 h-6 rounded-full border-2 border-[#14ABFE] border-t-transparent animate-spin" />
                </div>
            </OnboardingLayout>
        );
    }

    return (
        <OnboardingLayout
            currentStep={"aliases" satisfies OnboardingStep}
            completedSteps={["basic", "emails", "phone"]}
            title="Aliases"
            subtitle="Click on Field to Edit"
            onDashboardNavigate={() => navigate("/scanning-started")}
            footer={
                <div className="flex w-full gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/onboarding/phone-numbers")}
                        className={cx(
                            "flex h-[52px] flex-1 items-center justify-center rounded-xl text-sm font-semibold outline-none transition",
                            "border border-[var(--border-subtle)] dark:border-[#1E3A52]",
                            "text-[#0B1B2B] dark:text-white bg-transparent hover:bg-state-hover dark:hover:bg-white/10",
                            "focus-visible:ring-2 focus-visible:ring-[#14ABFE] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                        )}
                    >
                        &lt; BACK
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirmAndContinue}
                        className={cx(
                            "flex h-[52px] flex-1 items-center justify-center rounded-xl text-sm font-semibold text-white outline-none transition",
                            "bg-[#14ABFE] hover:bg-[#0E9AE8]",
                            "focus-visible:ring-2 focus-visible:ring-[#14ABFE] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                        )}
                    >
                        CONFIRM &gt;
                    </button>
                </div>
            }
        >
            {items.length === 0 && (
                <div className={cx(
                    "rounded-xl border p-6 text-center",
                    "bg-[var(--bg-surface)] dark:bg-[#112538]",
                    "border-[var(--border-subtle)] dark:border-[#1E3A52]",
                )}>
                    <p className="text-sm text-[var(--text-secondary)] dark:text-[#94A3B8] mb-4">
                        No aliases found from your scan. Add any nicknames, maiden names, or alternate spellings of your name.
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-4">
                {items.map((item) => (
                    <OnboardingDataCard
                        key={item.id}
                        value={item.name}
                        status={item.status}
                        isExpanded={activeId === item.id}
                        isEditing={editingId === item.id}
                        editContent={
                            editingId === item.id ? (
                                <EditInput
                                    id={`alias-${item.id}`}
                                    label="Alias name"
                                    value={editValue}
                                    onChange={setEditValue}
                                    placeholder="e.g. nickname, maiden name"
                                    icon={User}
                                />
                            ) : undefined
                        }
                        onClick={() => activeId === item.id ? setActiveId(null) : openEdit(item)}
                        onConfirmAndContinue={() => handleUpdate(item.id)}
                        showDelete
                        onDelete={() => handleDelete(item.id)}
                    />
                ))}
            </div>

            <div className="mt-8 flex justify-center pb-4">
                <button
                    type="button"
                    onClick={handleAdd}
                    className={cx(
                        "flex h-14 w-14 items-center justify-center rounded-full outline-none transition shadow-lg",
                        "bg-[#00D4AA] text-[#0B1B2B] hover:bg-[#00E5B8]",
                        "focus-visible:ring-2 focus-visible:ring-[#00D4AA] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#0B1B2B]",
                    )}
                    aria-label="Add Alias"
                >
                    <Plus className="h-6 w-6" aria-hidden />
                </button>
            </div>
        </OnboardingLayout>
    );
}
